import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { QRCodeModule } from 'angularx-qrcode';
import {
  CountryISO,
  NgxIntlTelInputModule,
  PhoneNumberFormat,
  SearchCountryField,
} from 'ngx-intl-tel-input-gg';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { catchError, concatMap, last, lastValueFrom, of, tap } from 'rxjs';
import { Clinic } from 'src/app/models/clinic.model';
import { PhoneNumber } from 'src/app/models/phone.model';
import { ClinicService } from 'src/app/services/clinic.service';
import {
  generatePreview,
  generateSlug,
  removeCountryCode,
  validateImage,
} from 'src/app/services/utils.service';

@Component({
  selector: 'clinic-table',
  standalone: true,
  imports: [
    TableModule,
    ImageModule,
    ButtonModule,
    CommonModule,
    DialogModule,
    ReactiveFormsModule,
    InputTextModule,
    ToastModule,
    InputTextareaModule,
    QRCodeModule,
    ColorPickerModule,
    ImageModule,
    FileUploadModule,
    NgxIntlTelInputModule,
    ConfirmDialogModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './clinic-table.component.html',
})
export class ClinicsTableComponent implements OnInit {
  @Input()
  data: Clinic[];

  appName: string = localStorage.getItem('appName');

  cols: any[];
  itemsActions: MenuItem[] = [];
  clinic: Clinic;
  isExpanded: boolean = false;
  editModal: boolean = false;
  loading: boolean = false;
  form: FormGroup;

  selectedFile: File | null;
  previewUrl: string | ArrayBuffer | null = null;

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  onlyCountries: CountryISO[] = [CountryISO.Spain];

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private clinicService = inject(ClinicService);
  private storage = inject(AngularFireStorage);

  constructor() {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      phone: [null as PhoneNumber | null, [Validators.required]],
      privacyLink: ['', [Validators.required]],
      color: [''],
      fileImage: new FormControl(null, [validateImage]),
    });
  }

  ngOnInit(): void {
    this.itemsActions = [
      { label: 'Ver especialistas', icon: 'pi pi-users' },
      { label: 'Ver usuarios', icon: 'pi pi-users' },
    ];
  }

  viewSpecialists(clinic: Clinic) {
    this.router.navigate(['/clinics', clinic.slug, 'specialists']);
  }

  viewUsers(clinic: Clinic) {
    this.router.navigate(['/clinics', clinic.slug, 'users']);
  }

  viewVisits(clinic: Clinic) {
    this.router.navigate(['/clinics', clinic.slug, 'visits']);
  }

  openModalEdit(clinic: Clinic) {
    this.clinic = { ...clinic };
    this.previewUrl = '';
    this.form.patchValue({
      name: clinic.name,
      phone: removeCountryCode(clinic.phone),
      privacyLink: clinic.privacyLink,
      color: clinic.color ?? '#ffffff',
    });
    this.editModal = true;
  }

  onDelete(clinic: Clinic) {
    this.confirmationService.confirm({
      key: 'onDelete',
      message: `¿Está seguro que desea eliminar la clínica ${clinic.name}?`,
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      accept: () => {
        this.clinicService.deleteClinic(clinic);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Clínica borrada',
          life: 3000,
        });
      },
      reject: () => {},
    });
  }

  async save() {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }
    this.loading = true;
    const changes = this.form.value as Partial<Clinic>;
    changes.phone = this.form.value.phone.e164Number;
    changes.slug = generateSlug(changes.name);

    if (this.selectedFile) {
      const filePath = `clinics/${this.clinic.id}/logo`;
      const task = this.storage.upload(filePath, this.selectedFile);
      await lastValueFrom(
        task.snapshotChanges().pipe(
          last(),
          concatMap(() => this.storage.ref(filePath).getDownloadURL()),
          tap((url) => (this.previewUrl = url)),
          catchError((err) => {
            console.log(err);
            alert('dio error');
            return of(null);
          }),
        ),
      );
      changes.image = this.previewUrl as string;
      this.updateClinic(changes);
      return;
    }

    this.updateClinic(changes);
  }

  private updateClinic(changes: Partial<Clinic>) {
    this.clinicService
      .update(this.clinic.id, changes)
      .pipe(
        tap((item) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Clínica actualizada',
            life: 3000,
          });
          this.editModal = false;
          this.loading = false;
          this.form.reset();
        }),
        catchError((error) => {
          this.loading = false;
          console.error('Error al actualizar ', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  onUpload(event: FileUploadHandlerEvent) {
    this.form.patchValue({ fileImage: event.files[0] });
    this.selectedFile = event.files[0];
    generatePreview(this.selectedFile).subscribe({
      next: (previewUrl) => {
        this.previewUrl = previewUrl;
      },
    });
  }
}
